import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H2 } from "../../../styles/Fonts";
import Button from "../../../components/Button";
import DepositModal from "./DepositModal";
import WithdrawModal from "./WithdrawModal";
import { getTruncNum } from "../../../utils/common";
import { VaultProps } from "../../../utils/constant/vaults";
import { useWeb3React } from "@web3-react/core";
import useTokenBalance from "../../../hooks/useTokenBalance";
import useAccountVaultBalance from "../../../hooks/useAccountVaultBalance";
import { useQuery } from "../../../hooks";
import SelectBridgeModal from "../../../components/SelectBridgeModal";

const TokenItem: React.FC<{ token: VaultProps }> = ({ token }) => {
  const {
    tokenAddress,
    tokenDecimal,
    tokenSymbol,
    tokenIcon,
    deployedChainId,
  } = token;

  const [depositModalIsShow, setDepositModalIsShow] = useState<boolean>(false);
  const [isDepositing, setIsDepositing] = useState<boolean>(false);
  const [withdrawModalIsShow, setWithdrawModalIsShow] =
    useState<boolean>(false);
  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
  const [bridgeModalIsShow, setBridgeModalIsShow] = useState<boolean>(false);

  const { account, chainId } = useWeb3React();
  const { tokenBalance, updateTokenBalance } = useTokenBalance({
    tokenAddress,
    tokenDecimal,
  });
  const { accountVaultBalance, updateAccountVaultBalance } =
    useAccountVaultBalance(tokenAddress, tokenDecimal, deployedChainId);
  let query = useQuery();

  const queryTokenAddress = query.get("token");

  useEffect(() => {
    if (queryTokenAddress === tokenAddress) {
      setTimeout(() => setDepositModalIsShow(true), 500);
    }
  }, [queryTokenAddress, tokenAddress]);

  const updateBalances = useCallback(() => {
    updateTokenBalance();
    updateAccountVaultBalance();
  }, [updateTokenBalance, updateAccountVaultBalance]);

  useEffect(() => {
    if (!account || !chainId) {
      return;
    }
    updateBalances();
  }, [account, chainId, tokenAddress, tokenDecimal, updateBalances]);

  return (
    <Container>
      <FBalanceContainer>
        <TokenRow>
          <TokenIcon src={tokenIcon} />
          <TokenName>{tokenSymbol}</TokenName>
        </TokenRow>
        <TokenBalance>{`${getTruncNum(
          accountVaultBalance,
          tokenDecimal
        )} ${tokenSymbol}`}</TokenBalance>
        <BtnsContainer>
          <Button
            btnText="Deposit"
            onClick={() => setDepositModalIsShow(true)}
            disabled={isDepositing}
            isLoading={isDepositing}
            btnType={"primary"}
          />
          <Button
            btnText="Withdraw"
            onClick={() => setWithdrawModalIsShow(true)}
            disabled={isWithdrawing}
            isLoading={isWithdrawing}
            btnType={"secondary"}
          />
        </BtnsContainer>
      </FBalanceContainer>
      <DepositModal
        setShowModal={setDepositModalIsShow}
        isShowModal={depositModalIsShow}
        setIsDepositing={setIsDepositing}
        currency={tokenSymbol}
        tokenAddress={tokenAddress}
        tokenDecimal={tokenDecimal}
        tokenBalance={tokenBalance}
        updateBalances={updateBalances}
        deployedChainId={deployedChainId}
        setBridgeModalIsShow={() => {
          setDepositModalIsShow(false);
          setBridgeModalIsShow(true);
        }}
      />
      <WithdrawModal
        setShowModal={setWithdrawModalIsShow}
        isShowModal={withdrawModalIsShow}
        setIsWithdrawing={setIsWithdrawing}
        currency={tokenSymbol}
        tokenAddress={tokenAddress}
        tokenDecimal={tokenDecimal}
        accountVaultBalance={accountVaultBalance}
        updateBalances={updateBalances}
      />
      <SelectBridgeModal
        targetTokenAddress={tokenAddress}
        deployedChainId={deployedChainId}
        show={bridgeModalIsShow}
        onHide={() => {
          setBridgeModalIsShow(false);
          setDepositModalIsShow(true);
        }}
      />
    </Container>
  );
};

export default TokenItem;

const Container = styled.div``;

const GrayContainer = styled.div`
  border-radius: 8px;
  padding: 24px;
  background-color: ${Colors.backgroundGray1};
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FBalanceContainer = styled(GrayContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const TokenRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const TokenIcon = styled.img`
  height: 28px;
  margin-right: 12px;
  @media (max-width: 768px) {
    height: 20px;
    margin-right: 8px;
  }
`;

const TokenName = styled(H2)`
  font-size: 16px;
  line-height: 24px;
  color: ${Colors.ivory};
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const TokenBalance = styled(H2)`
  flex: 1;
  font-size: 16px;
  line-height: 24px;
  color: ${Colors.ivory};
  text-align: left;
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 576px) {
    text-align: right;
  }
`;

const BtnsContainer = styled.div`
  flex: 0 0 220px;
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 768px) {
    flex: 0 0 180px;
  }
  @media (max-width: 576px) {
    margin-top: 16px;
    min-width: 100%;
  }
  button {
    font-size: 15px;
    @media (max-width: 768px) {
      font-size: 13px;
      line-height: 18px;
    }
  }
`;
